<template>
  <div v-if="list.length > 0" class="global-banners">
    <!-- @static-testid-exception: generic region marker — callers query all banners by this testid, not by index -->
    <div v-for="banner in list" :key="banner.key" :data-testid="bannerTestId(banner)" class="global-banner">
      <NoSubscriptionBanner v-if="banner.key === 'subscription_inactive' && isStructured(banner)" :banner="(banner as unknown as InactiveBannerPayload)" />
      <SetupFeeOwedBanner v-else-if="banner.key === 'setup_fee_owed' && isStructured(banner)" :banner="(banner as unknown as SetupFeeOwedBannerPayload)" />
      <GraceBanner v-else-if="isStructured(banner)" :banner="(banner as GraceBannerPayload)" />
      <template v-else>{{ banner.message }}</template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalBanners, type GlobalBanner } from '../composables/useGlobalBanners'
import GraceBanner from './GraceBanner.vue'
import NoSubscriptionBanner from './NoSubscriptionBanner.vue'
import SetupFeeOwedBanner from './SetupFeeOwedBanner.vue'

interface GraceItem {
  franchise_name: string
  days_left: number | null
  franchise_id: number
  status: string
}

interface GraceBannerPayload extends GlobalBanner {
  items: GraceItem[]
  severity: 'warn' | 'error'
}

interface InactiveItem {
  franchise_name: string
  franchise_id: number
  status: string | null
  days_left: number | null
}

interface InactiveBannerPayload extends GlobalBanner {
  items: InactiveItem[]
  severity: 'error'
}

interface SetupOwedItem {
  franchise_name: string
  franchise_id: number
  status: string
}

interface SetupFeeOwedBannerPayload extends GlobalBanner {
  items: SetupOwedItem[]
  severity: 'warn'
}

const { list } = useGlobalBanners()

function isStructured(banner: GlobalBanner): banner is GraceBannerPayload {
  return Array.isArray((banner as Record<string, unknown>).items)
}

/**
 * Fase A: the 'subscription_inactive' banner gets its OWN wrapper testid —
 * pre-existing callers of 'subscription-banner' were written when only the
 * grace banner existed and assert its (ab)sence specifically (e.g. "cancelled
 * row → no subscription-banner"), which must keep meaning "no GRACE banner",
 * not "no banner of any kind" now that a second, independent banner type
 * exists.
 */
function bannerTestId(banner: GlobalBanner): string {
  if (banner.key === 'subscription_inactive') return 'subscription-inactive-banner'
  if (banner.key === 'setup_fee_owed') return 'setup-fee-owed-banner-host'
  return 'subscription-banner'
}
</script>

<style scoped>
/* Plain-message banners get the warn look here; structured banners
   (GraceBanner) carry their own internals. Without this the message rendered
   as bare unstyled text — which, combined with the layout bug fixed in
   theme.css (§ app-shell: banners clear the fixed topbar), showed up as a
   blank white strip pushing the menu down while the text hid BEHIND the
   topbar. This area is SUBSCRIPTION/GRACE territory: credit warnings moved
   into the assistant sidebar (user decision 2026-08-12). */
.global-banner {
  padding: 8px 16px;
  background: #fff3cd;
  color: #664d03;
  border-bottom: 1px solid #ffe69c;
  font-size: 14px;
  line-height: 1.4;
}

/* GraceBanner/NoSubscriptionBanner paint their own surface — no double padding around them. */
.global-banner:has(.grace-banner, .no-subscription-banner, .setup-fee-owed-banner) {
  padding: 0;
  background: transparent;
  border-bottom: none;
}
</style>
